import React, { useEffect, useState } from 'react';
import api from '../api';
import Avatar from '../components/Avatar';
import { useAuth } from '../context/AuthContext';

// Matches MIN_PASSWORD_LENGTH in backend/src/routes/auth.js and routes/coaches.js.
// The SERVER enforces it; this is the courtesy check in front of it.
const MIN_PASSWORD = 8;

// The club's people: coaches AND admins, in one list.
//
// IT USED TO BE COACHES ONLY, and that was the bug. Promoting somebody made them
// disappear from the only list in the portal, and no screen or route could reach
// their row again — so there was no way to demote them, reset them or remove
// them. The role is now a column you can see and a button you can press.
//
// FOUR THINGS AN ADMIN CAN DO TO SOMEBODY, and the screen's whole job is making
// it obvious which is which BEFORE the click, not after:
//
//   Reset password   they are signed out everywhere and must choose a new one
//   Make / remove admin
//   Deactivate       reversible, keeps every record
//   Delete           NOT reversible, and only possible when there is nothing to
//                    lose, which the server checks before the admin commits
const Coaches = () => {
  const { user: me } = useAuth();
  const [people, setPeople] = useState([]);
  const [form, setForm] = useState({ name: '', email: '', phone: '', hourly_rate: '', temp_password: '', role: 'coach' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  // Keyed by coach id. Loaded alongside the list rather than per row, and
  // reloaded whenever the list is, so removing a photo shows immediately.
  const [avatars, setAvatars] = useState({});

  // The one open confirmation. { mode, person, check?, password? }
  // mode: 'reset' | 'deactivate' | 'delete' | 'role'
  const [panel, setPanel] = useState(null);
  const [panelBusy, setPanelBusy] = useState(false);
  const [panelError, setPanelError] = useState('');

  const load = async () => {
    const [list, pics] = await Promise.all([
      api.get('/api/coaches'),
      // A failure here must not take the list down with it. Worst case the page
      // shows initials.
      api.get('/api/coaches/avatars').catch(() => ({ data: [] }))
    ]);
    setPeople(list.data);
    const map = {};
    (pics.data || []).forEach(a => { map[a.coach_id] = a.small; });
    setAvatars(map);
  };
  useEffect(() => { load(); }, []);

  const closePanel = () => { setPanel(null); setPanelError(''); setPanelBusy(false); };

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    try {
      await api.post('/api/coaches', form);
      setSuccess(
        `${form.name} added as ${form.role === 'admin' ? 'an admin' : 'a coach'}. `
        + 'Give them the temporary password in person or on the phone, not in writing. '
        + 'The portal will make them choose their own as soon as they sign in.'
      );
      setForm({ name: '', email: '', phone: '', hourly_rate: '', temp_password: '', role: 'coach' });
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not add them');
    }
  };

  // An admin can take a photo down but never puts one up. There is no upload
  // control here and no server route for one — a coach's photo is theirs to
  // set, and this is only here so an unsuitable picture can be dealt with
  // without waiting for them.
  const removePhoto = async (person) => {
    if (!window.confirm(`Remove ${person.name}'s profile photo?`)) return;
    setError(''); setSuccess('');
    try {
      const res = await api.delete(`/api/coaches/${person.id}/photo`);
      setSuccess(res.data.message);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not remove that photo');
    }
  };

  // Reactivating is the harmless direction, so it needs no confirmation.
  const reactivate = async (person) => {
    setError(''); setSuccess('');
    try {
      await api.patch(`/api/coaches/${person.id}`, { active: true });
      setSuccess(`${person.name} can sign in again.`);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not reactivate that account');
    }
  };

  const openDelete = async (person) => {
    setError(''); setSuccess('');
    // Ask the server what deleting them WOULD do, and show the answer before
    // there is anything to click. The alternative is letting the admin press
    // Delete and then explaining why it was refused, which is the wrong way
    // round for the one button in this portal that destroys something.
    setPanel({ mode: 'delete', person, check: null });
    try {
      const res = await api.get(`/api/coaches/${person.id}/deletion-check`);
      setPanel({ mode: 'delete', person, check: res.data });
    } catch (err) {
      setPanel(null);
      setError(err.response?.data?.error || 'Could not check that account');
    }
  };

  const doDelete = async () => {
    setPanelError(''); setPanelBusy(true);
    try {
      const res = await api.delete(`/api/coaches/${panel.person.id}`);
      closePanel();
      setSuccess(res.data.message);
      await load();
    } catch (err) {
      // The server checks again under lock, so this can still refuse even after
      // the pre-flight said yes — a submission landing in between, for instance.
      setPanelError(err.response?.data?.error || 'That account could not be deleted');
      setPanelBusy(false);
    }
  };

  const doDeactivate = async (person) => {
    setPanelError(''); setPanelBusy(true);
    try {
      await api.patch(`/api/coaches/${person.id}`, { active: false });
      closePanel();
      setSuccess(`${person.name} has been deactivated. They are signed out now and cannot sign in again until you reactivate them. Everything they submitted is kept.`);
      await load();
    } catch (err) {
      setPanelError(err.response?.data?.error || 'That account could not be deactivated');
      setPanelBusy(false);
    }
  };

  const doRole = async () => {
    setPanelError(''); setPanelBusy(true);
    const next = panel.person.role === 'admin' ? 'coach' : 'admin';
    try {
      await api.patch(`/api/coaches/${panel.person.id}`, { role: next });
      closePanel();
      setSuccess(next === 'admin'
        ? `${panel.person.name} is now an admin. They can see every coach's hours and the bills.`
        : `${panel.person.name} is now an ordinary coach. Their admin screens are gone as of their next click.`);
      await load();
    } catch (err) {
      setPanelError(err.response?.data?.error || 'That role could not be changed');
      setPanelBusy(false);
    }
  };

  const doReset = async (e) => {
    e.preventDefault();
    setPanelError('');
    const pw = panel.password || '';
    if (pw.length < MIN_PASSWORD) {
      setPanelError(`The temporary password must be at least ${MIN_PASSWORD} characters. Three random words is an easy way to get there.`);
      return;
    }
    if (panelBusy) return;
    setPanelBusy(true);
    try {
      const res = await api.post(`/api/coaches/${panel.person.id}/reset-password`, { new_password: pw });
      closePanel();
      setSuccess(res.data.message);
      await load();
    } catch (err) {
      setPanelError(err.response?.data?.error || 'That password could not be reset');
      setPanelBusy(false);
    }
  };

  const isMe = (person) => person.id === me.id;
  const activeAdmins = people.filter(p => p.role === 'admin' && p.active).length;
  // The same rule the server enforces, mirrored here only so a button that
  // cannot work is not offered. THE SERVER IS WHAT ENFORCES IT.
  const lastAdmin = (person) => person.role === 'admin' && person.active && activeAdmins <= 1;

  return (
    <>
      <h1>Coaches and admins</h1>

      <div className="card">
        <h2>Add somebody</h2>
        {error && <div className="error-msg prewrap">{error}</div>}
        {success && <div className="success-msg prewrap">{success}</div>}
        <form onSubmit={submit}>
          <div className="form-grid">
            <div className="form-row">
              <label htmlFor="new-person-role">Coach or admin</label>
              {/* One word per option on purpose. The grid column is 180px at its
                  narrowest and a closed select clips whatever will not fit, so
                  "Admin — approves hours and raises bills" showed on screen as
                  "Admin — approves hour". The difference between the two is
                  explained in the line underneath instead, where there is room
                  for it. */}
              <select
                id="new-person-role"
                name="role"
                value={form.role}
                onChange={e => setForm({ ...form, role: e.target.value })}
              >
                <option value="coach">Coach</option>
                <option value="admin">Admin</option>
              </select>
              <p className="muted field-hint">
                {form.role === 'admin'
                  ? 'Approves hours, raises the bills and manages logins.'
                  : 'Logs their own hours and mileage, and sees nobody else’s.'}
              </p>
            </div>
            <div className="form-row">
              <label htmlFor="new-coach-name">Name</label>
              <input id="new-coach-name" name="name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required />
            </div>
            <div className="form-row">
              <label htmlFor="new-coach-email">Email</label>
              <input id="new-coach-email" name="email" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required />
            </div>
            <div className="form-row">
              <label htmlFor="new-coach-phone">Phone</label>
              <input id="new-coach-phone" name="phone" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
            </div>
            {/* An admin cannot log hours, so asking what to pay them per hour
                would be asking a question with no meaning. */}
            {form.role === 'coach' && (
              <div className="form-row">
                <label htmlFor="new-coach-rate">Hourly rate (£, blank = default)</label>
                <input id="new-coach-rate" name="hourly_rate" type="number" step="0.01" value={form.hourly_rate} onChange={e => setForm({ ...form, hourly_rate: e.target.value })} />
              </div>
            )}
            <div className="form-row">
              <label htmlFor="new-coach-temp">Temporary password</label>
              {/* type="password" so it is not left on screen in a club office.

                  autoComplete="off" here is a DELIBERATE, NARROW exception to the
                  house rule of 10/09/2026, approved by Daniel on 05/10/2026.

                  That rule exists so a person's browser can save and fill THEIR
                  OWN credential. This box holds neither. It is an admin typing a
                  third party's throwaway password into an admin screen. Marked
                  "new-password", Chrome offers to save the COACH's temporary
                  password against the ADMIN's own login for this site, which can
                  overwrite the admin's real saved password — worse than the thing
                  the rule prevents.

                  The exception covers ONLY this box and the reset box below.
                  Every genuine credential form (sign in, change password, reset
                  password) keeps its proper autocomplete and must never get "off".

                  It is also temporary: once email sending is live, adding a coach
                  sends an invite link instead of an admin typing a password, and
                  this box disappears. */}
              <input
                id="new-coach-temp"
                name="temp_password"
                type="password"
                autoComplete="off"
                value={form.temp_password}
                onChange={e => setForm({ ...form, temp_password: e.target.value })}
                required
                minLength={MIN_PASSWORD}
              />
            </div>
          </div>
          <button className="btn" type="submit">{form.role === 'admin' ? 'Add admin' : 'Add coach'}</button>
        </form>
        <p className="muted" style={{ marginBottom: 0 }}>
          They will be asked to choose their own password the first time they sign in, and nothing else
          in the portal opens up until they have.
        </p>
      </div>

      {/* ---- The one open confirmation ------------------------------------ */}
      {panel && (
        <div className="card panel">
          {panelError && <div className="error-msg prewrap">{panelError}</div>}

          {panel.mode === 'reset' && (
            <>
              <h2>Reset {panel.person.name}'s password</h2>
              <p className="muted" style={{ marginTop: 0 }}>
                Type a temporary password and tell them what it is in person or on the phone. Do not text
                or email it. As soon as you save this:
              </p>
              <ul className="muted panel-list">
                <li>they are signed out everywhere they are currently signed in;</li>
                <li>their old password stops working;</li>
                <li>the portal makes them choose their own password before they can do anything else.</li>
              </ul>
              <form onSubmit={doReset} className="password-form">
                <div className="form-row">
                  <label htmlFor="reset-temp-password">Temporary password</label>
                  <input
                    id="reset-temp-password"
                    name="newTempPassword"
                    type="password"
                    autoComplete="off"
                    value={panel.password || ''}
                    onChange={e => { setPanel({ ...panel, password: e.target.value }); setPanelError(''); }}
                    autoFocus
                  />
                </div>
                <div className="panel-buttons">
                  <button className="btn" type="submit" disabled={panelBusy}>
                    {panelBusy ? 'Saving…' : 'Reset their password'}
                  </button>
                  <button className="btn secondary" type="button" onClick={closePanel} disabled={panelBusy}>Cancel</button>
                </div>
              </form>
            </>
          )}

          {panel.mode === 'deactivate' && (
            <>
              <h2>Deactivate {panel.person.name}?</h2>
              <ul className="panel-list">
                <li>They are <strong>signed out immediately</strong>, on their phone as well as anywhere else.</li>
                <li>They <strong>cannot sign in again</strong> until you reactivate them.</li>
                <li>Everything they have submitted and every bill is <strong>kept exactly as it is</strong>.</li>
                <li>You can <strong>undo this at any time</strong> with the Reactivate button.</li>
              </ul>
              <p className="muted">
                This is the one to use when somebody stops coaching. Delete is the destructive one and is
                only for an account that was created by mistake.
              </p>
              <div className="panel-buttons">
                <button className="btn" type="button" onClick={() => doDeactivate(panel.person)} disabled={panelBusy}>
                  {panelBusy ? 'Working…' : 'Deactivate them'}
                </button>
                <button className="btn secondary" type="button" onClick={closePanel} disabled={panelBusy}>Cancel</button>
              </div>
            </>
          )}

          {panel.mode === 'role' && (
            <>
              <h2>
                {panel.person.role === 'admin'
                  ? `Remove ${panel.person.name} as an admin?`
                  : `Make ${panel.person.name} an admin?`}
              </h2>
              {panel.person.role === 'admin' ? (
                <ul className="panel-list">
                  <li>They become an ordinary coach and lose the Submissions, Bills, Coaches, Venues,
                      Sessions and Fixtures screens, as of their next click.</li>
                  <li>They keep their account, their hours and their bills.</li>
                  <li>You can make them an admin again at any time.</li>
                </ul>
              ) : (
                <ul className="panel-list">
                  <li>They will be able to see <strong>every coach's hours and every bill</strong>, approve
                      submissions and add or remove people.</li>
                  <li>They keep their own hours and their own bills.</li>
                  <li>You can remove it again at any time, as long as there is more than one admin.</li>
                </ul>
              )}
              <div className="panel-buttons">
                <button className="btn" type="button" onClick={doRole} disabled={panelBusy}>
                  {panelBusy ? 'Working…' : (panel.person.role === 'admin' ? 'Remove admin' : 'Make admin')}
                </button>
                <button className="btn secondary" type="button" onClick={closePanel} disabled={panelBusy}>Cancel</button>
              </div>
            </>
          )}

          {panel.mode === 'delete' && !panel.check && (
            <p className="muted" style={{ margin: 0 }}>Checking what {panel.person.name} has on record…</p>
          )}

          {panel.mode === 'delete' && panel.check && !panel.check.can_delete && (
            <>
              <h2>{panel.person.name} cannot be deleted</h2>
              <div className="error-msg prewrap">{panel.check.blocked_reason}</div>
              <table className="panel-counts">
                <tbody>
                  <tr><th>Submissions</th><td>{panel.check.counts.submissions}</td></tr>
                  <tr><th>Bills</th><td>{panel.check.counts.bills}</td></tr>
                  <tr><th>Submissions they approved</th><td>{panel.check.counts.approvals}</td></tr>
                  <tr><th>Bills they raised</th><td>{panel.check.counts.bills_created}</td></tr>
                </tbody>
              </table>
              <div className="panel-buttons">
                {panel.person.active && !panel.check.last_active_admin && !panel.check.is_self && (
                  <button className="btn" type="button" onClick={() => doDeactivate(panel.person)} disabled={panelBusy}>
                    {panelBusy ? 'Working…' : 'Deactivate them instead'}
                  </button>
                )}
                <button className="btn secondary" type="button" onClick={closePanel} disabled={panelBusy}>Close</button>
              </div>
            </>
          )}

          {panel.mode === 'delete' && panel.check && panel.check.can_delete && (
            <>
              <h2>Delete {panel.person.name}?</h2>
              <ul className="panel-list">
                <li>Their account and their sign-in are <strong>removed completely</strong>.</li>
                <li>They have <strong>no submissions and no bills</strong>, so nothing about what the club
                    has paid is lost.</li>
                <li><strong>This cannot be undone.</strong> There is no restore button.</li>
              </ul>
              <p className="muted">
                If they have ever coached, or might again, use Deactivate instead. That keeps the account and
                the records and can be reversed.
              </p>
              <div className="panel-buttons">
                <button className="btn danger" type="button" onClick={doDelete} disabled={panelBusy}>
                  {panelBusy ? 'Deleting…' : `Delete ${panel.person.name} for good`}
                </button>
                {panel.person.active && (
                  <button className="btn secondary" type="button" onClick={() => doDeactivate(panel.person)} disabled={panelBusy}>
                    Deactivate instead
                  </button>
                )}
                <button className="btn secondary" type="button" onClick={closePanel} disabled={panelBusy}>Cancel</button>
              </div>
            </>
          )}
        </div>
      )}

      <div className="card">
        <h2>Everybody with a login</h2>
        <p className="muted" style={{ marginTop: 0 }}>
          Admins see every coach's hours and the bills. Coaches see only their own.
        </p>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Name</th><th>Email</th><th>Phone</th><th>Role</th><th>Rate</th>
                <th>Invoicing</th><th>Status</th><th>Photo</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {people.map(p => (
                <tr key={p.id}>
                  <td>
                    <span className="cell-with-avatar">
                      <Avatar name={p.name} src={avatars[p.id]} size="sm" />
                      {p.name}{isMe(p) && <span className="muted"> (you)</span>}
                    </span>
                  </td>
                  <td>{p.email}</td>
                  <td>{p.phone || '—'}</td>
                  <td>
                    <span className={`badge ${p.role === 'admin' ? 'approved' : 'draft'}`}>
                      {p.role === 'admin' ? 'Admin' : 'Coach'}
                    </span>
                  </td>
                  <td>{p.hourly_rate === null || p.hourly_rate === undefined ? '—' : `£${Number(p.hourly_rate).toFixed(2)}/hr`}</td>
                  {/* What is missing before this coach can be invoiced, so the
                      gaps can be chased before a roll-up rather than after.
                      The account number is the last four digits and that is
                      all: the full one lives on the invoice, which is the one
                      screen that needs it. An admin is not on this list for
                      invoicing purposes, so theirs stays blank. */}
                  <td>
                    {p.role !== 'coach' ? <span className="muted">—</span> : (
                      <div className="invoicing-cell">
                        <div className={p.has_billing_address ? 'muted' : 'missing'}>
                          {p.has_billing_address ? 'Address given' : 'No address'}
                        </div>
                        <div className={p.bank_masked ? 'muted' : 'missing'}>
                          {p.bank_masked ? p.bank_masked.account_number : 'No bank details'}
                        </div>
                      </div>
                    )}
                  </td>
                  <td>
                    {p.active ? 'Active' : 'Inactive'}
                    {p.must_change_password && <div className="muted">must change password</div>}
                  </td>
                  <td>
                    <span className="photo-cell">
                      {avatars[p.id]
                        ? <button className="btn small secondary" onClick={() => removePhoto(p)}>Remove</button>
                        : <span className="muted">None</span>}
                    </span>
                  </td>
                  <td>
                    <div className="row-actions">
                      <button className="btn small secondary" type="button"
                        onClick={() => { setPanelError(''); setPanel({ mode: 'reset', person: p, password: '' }); }}>
                        Reset password
                      </button>

                      {!isMe(p) && !lastAdmin(p) && (
                        <button className="btn small secondary" type="button"
                          onClick={() => { setPanelError(''); setPanel({ mode: 'role', person: p }); }}>
                          {p.role === 'admin' ? 'Remove admin' : 'Make admin'}
                        </button>
                      )}

                      {p.active
                        ? (!isMe(p) && !lastAdmin(p) && (
                            <button className="btn small secondary" type="button"
                              onClick={() => { setPanelError(''); setPanel({ mode: 'deactivate', person: p }); }}>
                              Deactivate
                            </button>
                          ))
                        : (
                          <button className="btn small secondary" type="button" onClick={() => reactivate(p)}>
                            Reactivate
                          </button>
                        )}

                      {!isMe(p) && !lastAdmin(p) && (
                        <button className="btn small danger" type="button" onClick={() => openDelete(p)}>
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {people.length === 0 && (
                <tr><td colSpan={8} className="muted">Nobody has a login yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <p className="muted" style={{ marginBottom: 0 }}>
          There is no Make admin, Deactivate or Delete button against your own row, or against the only
          active admin. Both would lock the club out of its own portal.
        </p>
      </div>
    </>
  );
};

export default Coaches;
